import React, { useEffect, useState } from "react";

import { useOktaAuth } from "@okta/okta-react";
import { Button, Card, CardHeader, Field, Input, Link, Spinner, Text } from "@fluentui/react-components";
import { ArrowLeft12Filled, BuildingShopRegular } from "@fluentui/react-icons";

import { useLoginOktaStyles } from "./styles";

import { useAuth } from "@/context/auth";
import { useOkta } from "@/context/okta";
import { useRoute } from "@/context/router";

export const LoginOkta: React.FC = () => {
  const { oktaConfig } = useOkta();
  const { oktaAuth } = useOktaAuth();

  const { setCurrentRoute } = useRoute();
  const { isLoadingLogin, loadCustomer, loginWithOkta } = useAuth();

  const [error, setError] = useState<string>("");
  const [customerName, setCustomerName] = useState<string>("");

  const styles = useLoginOktaStyles();

  useEffect(() => {
    // Trigger the login popup when Okta client has reloaded
    if (
      oktaAuth.options.clientId === undefined ||
      oktaConfig.clientId === undefined ||
      oktaAuth.options.clientId !== oktaConfig.clientId
    ) {
      return;
    }
    handleLogin();
  }, [oktaAuth, oktaConfig]);

  const handleSubmit: React.FormEventHandler<HTMLFormElement> = async (e) => {
    e.preventDefault();
    try {
      setError("");
      await loadCustomer(customerName);
    } catch (error) {
      setError(
        error?.response?.data?.error ||
          error?.message ||
          "Loading organization failed. Please check your organization name."
      );
    }
  };

  const handleLogin = async () => {
    try {
      setError("");
      await loginWithOkta();
      setCurrentRoute("risk-analysis");
    } catch (error) {
      setError(error?.message || "Login with Okta failed. Please check your Okta configuration.");
    }
  };

  return (
    <div className={styles.container}>
      <Button
        appearance="transparent"
        icon={<ArrowLeft12Filled />}
        className={styles.backButton}
        onClick={() => setCurrentRoute("login")}
      >
        Back
      </Button>
      <Card className={styles.card}>
        <CardHeader
          header={
            <div className={styles.header}>
              <Text weight="semibold" size={500}>
                Log In with Okta
              </Text>
            </div>
          }
        />
        <form className={styles.form} onSubmit={handleSubmit}>
          <Field required label="Organization">
            <Input
              required
              id="customerName"
              value={customerName}
              disabled={isLoadingLogin}
              className={styles.input}
              contentBefore={<BuildingShopRegular className={styles.icon} />}
              onChange={(e) => setCustomerName(e.target.value)}
            />
          </Field>
          <Text size={100} weight="regular">
            The organization is required and is case sensitive.
          </Text>
          <Button
            type="submit"
            appearance="primary"
            iconPosition="after"
            disabled={isLoadingLogin}
            className={styles.button}
          >
            Log In
            {isLoadingLogin && <Spinner size="extra-tiny" className={styles.loading} />}
          </Button>
          <Text size={100} weight="regular">
            Please contact your Okta account&apos;s admin or{" "}
            <Link href="mailto:sales@yellowpad.ai">sales@yellowpad.ai</Link> to get help if you don&apos;t know your
            organization&apos;s name.
          </Text>
          {error && <div className={styles.errorText}>{error}</div>}
        </form>
      </Card>
    </div>
  );
};

// TODO: keeping the okta widget related code in case it ever becomes necessary
// useEffect(() => {
//   if (!widgetRef.current) {
//     return () => {};
//   }

//   const widget = new OktaSignIn(config.widget);
//   widget
//     .showSignInToGetTokens({
//       el: widgetRef.current as unknown as string,
//     })
//     .then(async (res) => {
//       await oktaAuth.handleLoginRedirect(res);
//       await saveToStorage("okta_tokens", JSON.stringify(res));
//     })
//     .catch((error) => {
//       console.log("🚀 ~ React.useEffect ~ error:", error);
//     });

//   return () => widget.remove();
// }, []);
